import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { FilesControllerUploadDataImportFile200Response } from '../../../_generated';
import { FlowbiteService } from '../../services/flowbite.service';

@Component({
  selector: 'app-file-report',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    @if (report) {
      <div class="report-container p-6 bg-white rounded-lg shadow-md">
        <!-- Header with overall status -->
        <div class="header mb-6 sticky top-0 bg-white shadow">
          <div class="flex items-center justify-between">
            <h2 class="text-xl font-semibold">{{ 'report.fileProcessingResults' | translate }}</h2>
            <div [ngClass]="{ 'text-green-600': !hasErrors, 'text-red-600': hasErrors }" class="text-lg font-medium">
              {{ report.message }}
            </div>
          </div>
          <!-- Summary information -->
          @if (report.summary) {
            <div class="summary mt-4 p-4 bg-gray-50 rounded-lg">
              <h3 class="text-lg font-medium mb-2">{{ 'report.summary' | translate }}</h3>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div class="stat-item">
                  <div class="text-sm text-gray-600">{{ 'report.totalRows' | translate }}</div>
                  <div class="text-2xl font-semibold">{{ report.summary.totalRows }}</div>
                </div>
                <div class="stat-item">
                  <div class="text-sm text-gray-600">{{ 'report.processedRows' | translate }}</div>
                  <div class="text-2xl font-semibold">{{ report.summary.processedRows }}</div>
                </div>
                <div class="stat-item">
                  <div class="text-sm text-gray-600">{{ 'report.successCount' | translate }}</div>
                  <div class="text-2xl font-semibold text-green-600">{{ report.summary.successCount }}</div>
                </div>
                <div class="stat-item">
                  <div class="text-sm text-gray-600">{{ 'report.failureCount' | translate }}</div>
                  @if (report.summary.failureCount) {
                    <div
                      class="text-2xl font-semibold"
                      [ngClass]="{
                        'text-red-600': report.summary.failureCount > 0,
                        'text-gray-700': !report.summary.failureCount || report.summary.failureCount === 0,
                      }"
                    >
                      {{ report.summary.failureCount }}
                    </div>
                  }
                </div>
              </div>
            </div>
          }
        </div>
        <!-- Flowbite Tabs -->
        <div class="mb-4">
          <!-- Tabs navigation -->
          <ul
            class="flex flex-wrap text-sm font-medium text-center text-gray-500 border-b border-gray-200 dark:border-gray-700 dark:text-gray-400"
            id="defaultTab"
            role="tablist"
          >
            <li class="mr-2" role="presentation">
              <button
                class="inline-block p-4 rounded-t-lg"
                [ngClass]="{
                  'text-primary-600 bg-gray-100 dark:bg-gray-800 dark:text-primary-500 active': activeTab === 'success',
                  'hover:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:text-gray-300': activeTab !== 'success',
                }"
                type="button"
                role="tab"
                id="success-tab"
                data-tabs-target="#success-tab-content"
                aria-controls="success-tab-content"
                [attr.aria-selected]="activeTab === 'success'"
                (click)="setActiveTab('success')"
              >
                {{ 'report.successfulEntries' | translate }} ({{ report.results?.successfulEntries?.length || 0 }})
              </button>
            </li>
            <li class="mr-2" role="presentation">
              <button
                class="inline-block p-4 rounded-t-lg"
                [ngClass]="{
                  'text-primary-600 bg-gray-100 dark:bg-gray-800 dark:text-primary-500 active': activeTab === 'failures',
                  'hover:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:text-gray-300': activeTab !== 'failures',
                }"
                type="button"
                role="tab"
                id="failures-tab"
                data-tabs-target="#failures-tab-content"
                aria-controls="failures-tab-content"
                [attr.aria-selected]="activeTab === 'failures'"
                (click)="setActiveTab('failures')"
              >
                {{ 'report.failedEntries' | translate }} ({{ report.results?.failedEntries?.length || 0 }})
              </button>
            </li>
          </ul>
          <!-- Tab content -->
          <div id="defaultTabContent">
            @if (report.results) {
              <div>
                @if (report.results.successfulEntries) {
                  <div
                    class="p-4 rounded-lg bg-gray-50 dark:bg-gray-800"
                    [ngClass]="{ hidden: activeTab !== 'success' }"
                    id="success-tab-content"
                    role="tabpanel"
                    aria-labelledby="success-tab"
                  >
                    <!-- Successful Entries Tab Content -->
                    <div class="success-entries">
                      @if (!report.results.successfulEntries.length) {
                        <div class="text-center py-8 text-gray-500">
                          {{ 'report.noSuccessfulEntries' | translate }}
                        </div>
                      }
                      <!-- Table of successful entries -->
                      @if (report.results.successfulEntries && report.results.successfulEntries.length > 0) {
                        <div class="overflow-x-auto">
                          <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                              <tr>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  {{ 'report.row' | translate }}
                                </th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  {{ 'report.site' | translate }}
                                </th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  {{ 'report.shop' | translate }}
                                </th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  {{ 'report.tool' | translate }}
                                </th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  {{ 'report.emissionPoint' | translate }}
                                </th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  {{ 'report.parameter' | translate }}
                                </th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  {{ 'report.value' | translate }}
                                </th>
                              </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">
                              @for (entry of report.results.successfulEntries; track entry) {
                                <tr class="hover:bg-gray-50">
                                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ entry.row }}</td>
                                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ entry.site }}</td>
                                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ entry.shop }}</td>
                                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ entry.tool }}</td>
                                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ entry.emissionPoint }}</td>
                                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ entry.parameter }}</td>
                                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ entry.value }}</td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                      }
                    </div>
                  </div>
                }
                @if (report.results.failedEntries) {
                  <div
                    class="p-4 rounded-lg bg-gray-50 dark:bg-gray-800"
                    [ngClass]="{ hidden: activeTab !== 'failures' }"
                    id="failures-tab-content"
                    role="tabpanel"
                    aria-labelledby="failures-tab"
                  >
                    <!-- Failed Entries Tab Content -->
                    <div class="failed-entries">
                      @if (!report.results.failedEntries.length) {
                        <div class="text-center py-8 text-gray-500">
                          {{ 'report.noFailedEntries' | translate }}
                        </div>
                      }
                      <!-- List of failed entries with reasons -->
                      @if (report.results.failedEntries.length > 0) {
                        <div class="space-y-4">
                          @for (entry of report.results.failedEntries; track entry) {
                            <div class="border border-red-200 rounded-lg p-4 bg-red-50">
                              <div class="flex justify-between items-start">
                                <div class="font-medium text-red-600">{{ 'report.rowError' | translate }} {{ entry.row }}</div>
                              </div>
                              <div class="mt-2 text-sm text-gray-800">
                                <span class="font-medium">{{ 'report.reason' | translate }}:</span> {{ entry.reason }}
                              </div>
                              <div class="text-sm text-gray-500">{{ entry.data | json }}</div>
                            </div>
                          }
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>
            }
            <!-- Actions -->
            <div class="actions mt-6 flex justify-end">
              <button class="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-400" (click)="closeReport()">
                {{ 'common.close' | translate }}
              </button>
            </div>
          </div>
        </div>
      </div>
    }
  `,
  styles: `
    .stat-item {
      padding: 10px;
      border-radius: 8px;
      background-color: white;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    }
  `,
})
export class FileReportComponent implements OnInit, AfterViewInit {
  @Input() report!: FilesControllerUploadDataImportFile200Response;

  readonly #router = inject(Router);
  readonly #flowbiteService = inject(FlowbiteService);

  activeTab: 'success' | 'failures' = 'success';
  hasErrors = false;

  ngOnInit(): void {
    // Determine if there are errors
    if (this.report?.summary?.failureCount) {
      this.hasErrors = this.report.summary.failureCount > 0;
    }

    // Default to failure tab if there are errors
    if (this.hasErrors) {
      this.activeTab = 'failures';
    }
  }

  ngAfterViewInit(): void {
    // Initialize Flowbite to make tabs work
    this.#flowbiteService.reinitializeFlowbite();
  }

  setActiveTab(tab: 'success' | 'failures'): void {
    this.activeTab = tab;
  }

  closeReport(): void {
    // This could emit an event to close the report in the parent component
    this.#router.navigate(['/']);
  }
}
